import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { nameof } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { DeleteAction } from './DeleteAction';
import { DownloadAction } from './DownloadAction';
import { MoveAction } from './MoveAction';
import { RenameAction } from './RenameAction';
import { ViewAction } from './ViewAction';
import { formatFileSize } from './utils/format-file-size.util';
import { getDerivedFilesInfo } from './utils/get-derived-files-info.util';
import type { FileItem } from './types/file-item.type';

interface ColumnCallbacks {
  readonly canMove: boolean;
  readonly filesById: ReadonlyMap<Id, FileItem>;
  readonly onView: (file: FileItem) => void;
  readonly onRename: (file: FileItem) => void;
  readonly onMove: (file: FileItem) => void;
  readonly onDelete: (file: FileItem) => void;
}

export const getColumns = ({
  canMove,
  filesById,
  onView,
  onRename,
  onMove,
  onDelete,
}: Readonly<ColumnCallbacks>): readonly GridColDef<FileItem>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    width: 200,
    getActions: (params: Readonly<GridRowParams<FileItem>>) => [
      <ViewAction file={params.row} key="view" onView={onView} />,
      <DownloadAction file={params.row} key="download" />,
      <RenameAction file={params.row} key="rename" onRename={onRename} />,
      <MoveAction
        disabled={!canMove}
        file={params.row}
        key="move"
        onMove={onMove}
      />,
      <DeleteAction file={params.row} key="delete" onDelete={onDelete} />,
    ],
  },
  {
    field: nameof<FileItem>('originalName'),
    headerName: 'Nom du fichier',
    flex: 1,
    description: 'Nom du fichier',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<FileItem>('mimeType'),
    headerName: 'Type MIME',
    width: 200,
    description: 'Type MIME',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<FileItem>('size'),
    headerName: 'Taille',
    width: 120,
    description: 'Taille',
    editable: false,
    sortable: true,
    type: 'number',
    valueFormatter: formatFileSize,
  },
  {
    field: 'compressedSize',
    headerName: 'Version compressée',
    width: 160,
    description: 'Version compressée',
    editable: false,
    sortable: true,
    type: 'number',
    valueGetter: (_value, row: FileItem) =>
      getDerivedFilesInfo(row, filesById).compressedFile?.size ?? null,
    valueFormatter: (value: number | null) =>
      value === null ? 'Aucune' : formatFileSize(value),
  },
  {
    field: 'hasThumbnail',
    headerName: 'Vignette',
    width: 110,
    description: 'Vignette',
    editable: false,
    sortable: true,
    type: 'boolean',
    valueGetter: (_value, row: FileItem) =>
      getDerivedFilesInfo(row, filesById).hasThumbnail,
  },
];
